/**
 * LandingHeader - Navy + Gold accent
 * Active section indicator via IntersectionObserver
 */
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar, Toolbar, Button, Typography, Box, Stack,
  IconButton, Drawer, Container
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import XIcon from '@mui/icons-material/X';

import { COLORS, GRADIENTS, TRANSITIONS, RADIUS } from './landingTheme';

const SOCIAL_LINKS = [
  { icon: <InstagramIcon />, href: 'https://instagram.com/nara.health', label: 'Instagram' },
  { icon: <FacebookIcon />, href: 'https://facebook.com/nara.health', label: 'Facebook' },
  { icon: <XIcon />, href: 'https://x.com/narahealth', label: 'X' },
  { icon: <YouTubeIcon />, href: 'https://youtube.com/@narahealth', label: 'YouTube' },
];

// NARA Logo path
const LOGO_PATH = '/images/logo-nara-png.png';

// Section IDs mapped to nav labels
const NAV_ITEMS = [
  { label: 'Funcionalidades', sectionId: 'funcionalidades' },
  { label: 'Soluciones', sectionId: 'soluciones' },
  { label: 'Planes', sectionId: 'precios' },
  { label: 'Cómo empezar', sectionId: 'como-empezar' },
  { label: 'FAQ', sectionId: 'faq' },
  { label: 'Tecnología', sectionId: 'tecnologia' },
];

export default function LandingHeader() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Active section detection via IntersectionObserver
  useEffect(() => {
    const sectionIds = NAV_ITEMS.map((item) => item.sectionId);
    const observers = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(id);
          }
        },
        { rootMargin: '-40% 0px -55% 0px' }
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: scrolled
          ? 'rgba(15, 36, 64, 0.97)'
          : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: 'none',
        transition: TRANSITIONS.normal,
        boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.15)' : 'none',
      }}
    >
      {/* Pre-header bar — desktop only, hides on scroll */}
      <Box
        sx={{
          display: { xs: 'none', md: scrolled ? 'none' : 'block' },
          background: 'rgba(0, 0, 0, 0.2)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          transition: TRANSITIONS.fast,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.4,
            }}
          >
            {/* Email */}
            <Box
              component="a"
              href="mailto:soporte@nara.health"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
                color: 'rgba(255,255,255,0.45)',
                textDecoration: 'none',
                fontSize: '0.72rem',
                transition: TRANSITIONS.fast,
                '&:hover': { color: COLORS.accent },
              }}
            >
              <EmailOutlinedIcon sx={{ fontSize: 13 }} />
              soporte@nara.health
            </Box>

            {/* Social icons */}
            <Stack direction="row" spacing={0.3}>
              {SOCIAL_LINKS.map((social) => (
                <IconButton
                  key={social.label}
                  component="a"
                  href={social.href}
                  target="_blank"
                  rel="noopener"
                  aria-label={social.label}
                  size="small"
                  sx={{
                    color: 'rgba(255,255,255,0.35)',
                    p: 0.4,
                    transition: TRANSITIONS.fast,
                    '&:hover': { color: COLORS.accent, background: 'transparent' },
                    '& svg': { fontSize: 15 },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: 'space-between', py: 0.5, minHeight: '60px !important' }}>
          {/* Logo */}
          <Box
            component={RouterLink}
            to="/"
            sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box
              component="img"
              src={LOGO_PATH}
              alt="NARA"
              sx={{ height: 44, width: 'auto' }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.1rem',
                color: '#fff',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              NARA
            </Typography>
          </Box>

          {/* Desktop Nav */}
          {!isMobile && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {NAV_ITEMS.map((item) => {
                const isActive = activeSection === item.sectionId;
                return (
                  <Button
                    key={item.label}
                    onClick={() => scrollTo(item.sectionId)}
                    sx={{
                      color: isActive ? '#fbbf24' : 'rgba(255,255,255,0.8)',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.9rem',
                      textTransform: 'none',
                      px: 1.5,
                      py: 0.5,
                      minHeight: 0,
                      position: 'relative',
                      transition: TRANSITIONS.fast,
                      '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' },
                      // Active underline
                      '&::after': isActive ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 2,
                        left: '20%',
                        right: '20%',
                        height: 2,
                        borderRadius: 1,
                        background: '#fbbf24',
                      } : {},
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}

              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                sx={{
                  background: GRADIENTS.primaryButton,
                  color: '#0f2440',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  px: 2.5,
                  py: 0.75,
                  borderRadius: RADIUS.md,
                  boxShadow: '0 4px 14px rgba(245, 158, 11, 0.3)',
                  transition: TRANSITIONS.normal,
                  '&:hover': {
                    background: GRADIENTS.primaryButtonHover,
                    boxShadow: '0 6px 20px rgba(245, 158, 11, 0.4)',
                  },
                }}
              >
                Login
              </Button>
            </Stack>
          )}

          {/* Mobile */}
          {isMobile && (
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: '#fff', p: 0.5 }}>
              <MenuIcon />
            </IconButton>
          )}

          {/* Mobile Drawer */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{
              sx: {
                width: 300,
                p: 0,
                background: 'linear-gradient(180deg, #0f2440 0%, #1e3a5f 100%)',
                color: '#fff',
              },
            }}
          >
            {/* Header with logo */}
            <Box
              sx={{
                px: 2.5,
                pt: 2,
                pb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="img" src={LOGO_PATH} alt="NARA" sx={{ height: 32 }} />
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff' }}>NARA</Typography>
              </Box>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Nav items */}
            <Stack spacing={0.5} sx={{ px: 1.5, py: 2 }}>
              {NAV_ITEMS.map((item) => {
                const isActive = activeSection === item.sectionId;
                return (
                  <Button
                    key={item.label}
                    onClick={() => { scrollTo(item.sectionId); setDrawerOpen(false); }}
                    sx={{
                      justifyContent: 'flex-start',
                      color: isActive ? '#fbbf24' : 'rgba(255,255,255,0.75)',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.95rem',
                      textTransform: 'none',
                      py: 1.2,
                      px: 2,
                      borderRadius: RADIUS.md,
                      background: isActive ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                      borderLeft: isActive ? '3px solid #fbbf24' : '3px solid transparent',
                      transition: TRANSITIONS.fast,
                      '&:hover': {
                        background: 'rgba(255,255,255,0.06)',
                        color: '#fff',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>

            {/* CTA */}
            <Box sx={{ px: 2.5, py: 2.5 }}>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                fullWidth
                endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                sx={{
                  background: GRADIENTS.primaryButton,
                  color: '#0f2440',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  py: 1.3,
                  borderRadius: RADIUS.md,
                  boxShadow: '0 4px 20px rgba(245, 158, 11, 0.35)',
                  '&:hover': {
                    background: GRADIENTS.primaryButtonHover,
                    boxShadow: '0 8px 28px rgba(245, 158, 11, 0.45)',
                  },
                }}
              >
                Login
              </Button>

              {/* WhatsApp demo */}
              <Button
                component="a"
                href="https://wa.me/573052608968?text=Hola%2C%20me%20interesa%20una%20demo%20de%20NARA%20para%20mi%20consultorio"
                target="_blank"
                rel="noopener"
                fullWidth
                startIcon={<WhatsAppIcon sx={{ fontSize: '16px !important' }} />}
                sx={{
                  mt: 1.5,
                  color: 'rgba(255,255,255,0.7)',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  py: 1,
                  borderRadius: RADIUS.md,
                  border: '1px solid rgba(255,255,255,0.12)',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                Solicitar demo
              </Button>
            </Box>

            {/* Social icons at bottom */}
            <Box sx={{ mt: 'auto', px: 2.5, pb: 2.5 }}>
              <Stack direction="row" spacing={1} justifyContent="center">
                {SOCIAL_LINKS.map((social) => (
                  <IconButton
                    key={social.label}
                    component="a"
                    href={social.href}
                    target="_blank"
                    rel="noopener"
                    aria-label={social.label}
                    size="small"
                    sx={{
                      color: 'rgba(255,255,255,0.3)',
                      transition: TRANSITIONS.fast,
                      '&:hover': { color: '#fbbf24', background: 'rgba(245, 158, 11, 0.1)' },
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Stack>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
